import { t } from '@lingui/core/macro'
import { persistEvalDecision } from 'features/evaluations/metadata/utils/persistEvalDecision'
import { usePageMetadataEvalSession } from 'features/knowledgeDocumentViewer/contexts/PageMetadataEvalSessionContext'
import { useSaveDocumentPageMetadataUserEdit } from 'features/knowledgeDocumentViewer/hooks/useSaveDocumentPageMetadataUserEdit'
import type {
  DocumentPageMetadataUserEditStatus,
  DocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import { EVAL_PAGE_METADATA_USER_EDIT_WRITE } from 'features/knowledgeDocumentViewer/utils/buildDocumentPageMetadataUserEditPut'
import { useInvalidateDocumentPageMetadataUserEditLoads } from 'features/knowledgeDocumentViewer/utils/invalidateDocumentPageMetadataUserEditLoads'
import { useGeckoNotifications } from 'features/notifications'
import { useCallback } from 'react'

export function usePageMetadataEvalActions({
  bqDocumentId,
  pageNum,
  pageEntry,
}: {
  bqDocumentId: string | undefined
  pageNum: number
  pageEntry: SystemDocumentPageEntry | undefined
}) {
  const { savePageMetadataUserEdit } = useSaveDocumentPageMetadataUserEdit()
  const invalidate = useInvalidateDocumentPageMetadataUserEditLoads()
  const { errorNotification } = useGeckoNotifications()
  const { currentPage, isBusy, runExclusive, markDecision, next } =
    usePageMetadataEvalSession()

  const persist = useCallback(
    async (
      status: DocumentPageMetadataUserEditStatus,
      values: DocumentPageMetadataUserEditValues,
      shouldHaveMetadata: boolean | undefined,
      commentText?: string,
      deferReason?: string
    ) => {
      if (!bqDocumentId || !currentPage || !pageEntry)
        throw new Error('Cannot save page metadata without a loaded page')
      await runExclusive(async () => {
        await persistEvalDecision({
          save: () =>
            savePageMetadataUserEdit({
              bqDocumentId,
              pageNum,
              systemPage: pageEntry,
              priorEdit: undefined,
              userValues: values,
              ...EVAL_PAGE_METADATA_USER_EDIT_WRITE,
              status,
              shouldHaveMetadata,
              commentText,
              deferReason,
              blindQa: currentPage.blind_qa === true,
              successMessage:
                status === 'confirmed' ? t`Page confirmed` : t`Page deferred`,
            }),
          markSaved: () => markDecision(currentPage.page_id, status),
          refresh: invalidate,
          onRefreshError: () =>
            errorNotification(
              t`Page saved, but updated metadata could not be refreshed`
            ),
          advance: () => next(true),
        })
      })
    },
    [
      bqDocumentId,
      currentPage,
      pageEntry,
      runExclusive,
      savePageMetadataUserEdit,
      pageNum,
      markDecision,
      invalidate,
      errorNotification,
      next,
    ]
  )

  const confirmPage = useCallback(
    (
      values: DocumentPageMetadataUserEditValues,
      shouldHaveMetadata: boolean | undefined,
      commentText?: string
    ) => persist('confirmed', values, shouldHaveMetadata, commentText),
    [persist]
  )
  const deferPage = useCallback(
    (
      reason: string,
      values: DocumentPageMetadataUserEditValues,
      shouldHaveMetadata: boolean | undefined,
      commentText?: string
    ) => persist('deferred', values, shouldHaveMetadata, commentText, reason),
    [persist]
  )
  return { isSaving: isBusy, confirmPage, deferPage }
}
